import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ImageUploadField from '../../components/ImageUploadField';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRallies = () => {
  const [rallies, setRallies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRally, setEditingRally] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    dates: '',
    route: '',
    description: '',
    includes: '',
    price: '',
    ticket_link: '',
    status: 'upcoming',
    image_url: '',
    image_media_type: 'image'
  });

  const fetchRallies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rallies`);
      setRallies(response.data);
    } catch (error) {
      toast.error('Failed to fetch rallies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRallies();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingRally(null);
    setFormData({
      name: '',
      dates: '',
      route: '',
      description: '',
      includes: '',
      price: '',
      ticket_link: '',
      status: 'upcoming',
      image_url: '',
      image_media_type: 'image'
    });
    setShowModal(true);
  };

  const openEditModal = (rally) => {
    setEditingRally(rally);
    setFormData({
      name: rally.name,
      dates: rally.dates,
      route: rally.route || '',
      description: rally.description || '',
      includes: rally.includes?.join('\n') || '',
      price: rally.price || '',
      ticket_link: rally.ticket_link || '',
      status: rally.status,
      image_url: rally.image_url || '',
      image_media_type: rally.image_media_type || 'image'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const payload = {
      ...formData,
      includes: formData.includes.split('\n').filter(item => item.trim())
    };

    try {
      if (editingRally) {
        await axios.put(`${API_URL}/api/rallies/${editingRally.id}`, payload, { headers });
        toast.success('Rally updated');
      } else {
        await axios.post(`${API_URL}/api/rallies`, payload, { headers });
        toast.success('Rally created');
      }
      setShowModal(false);
      fetchRallies();
    } catch (error) {
      toast.error('Failed to save rally');
    }
  };

  const handleDelete = async (rallyId) => {
    if (!window.confirm('Are you sure you want to delete this rally?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/rallies/${rallyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rally deleted');
      fetchRallies();
    } catch (error) {
      toast.error('Failed to delete rally');
    }
  };

  return (
    <div data-testid="admin-rallies">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">RALLIES</h1>
        <button
          onClick={openCreateModal}
          data-testid="add-rally-btn"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Rally
        </button>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : rallies.length === 0 ? (
        <p className="text-[#A19D98]">No rallies yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rallies.map((rally) => (
                <tr key={rally.id} data-testid={`rally-row-${rally.id}`}>
                  <td className="text-[#F2EDE6]">{rally.name}</td>
                  <td className="text-[#A19D98]">{rally.dates}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-['Oswald'] tracking-wider ${
                      rally.status === 'open' ? 'badge-open' :
                      rally.status === 'completed' ? 'badge-completed' :
                      'badge-coming-soon'
                    }`}>
                      {rally.status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(rally)}
                        data-testid={`edit-rally-${rally.id}`}
                        className="p-2 text-[#A19D98] hover:text-[#E8651A]"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(rally.id)}
                        data-testid={`delete-rally-${rally.id}`}
                        className="p-2 text-[#A19D98] hover:text-[#FF3B30]"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">
                {editingRally ? 'EDIT RALLY' : 'ADD RALLY'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="rally-name-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Dates *</label>
                  <Input
                    name="dates"
                    value={formData.dates}
                    onChange={handleInputChange}
                    placeholder="April 17-19, 2026"
                    required
                    data-testid="rally-dates-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Status</label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger data-testid="rally-status-select" className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#2A2A2A]">
                      <SelectItem value="upcoming" className="text-[#F2EDE6]">Upcoming</SelectItem>
                      <SelectItem value="open" className="text-[#F2EDE6]">Open</SelectItem>
                      <SelectItem value="coming_soon" className="text-[#F2EDE6]">Coming Soon</SelectItem>
                      <SelectItem value="completed" className="text-[#F2EDE6]">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Route</label>
                <Input
                  name="route"
                  value={formData.route}
                  onChange={handleInputChange}
                  placeholder="Houston → Austin"
                  data-testid="rally-route-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Description</label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  data-testid="rally-description-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Includes (one per line)</label>
                <Textarea
                  name="includes"
                  value={formData.includes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Hotel stay&#10;Meals&#10;Rally decals"
                  data-testid="rally-includes-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Price</label>
                  <Input
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="$500 or 'Pricing TBD'"
                    data-testid="rally-price-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Ticket Link</label>
                  <Input
                    name="ticket_link"
                    value={formData.ticket_link}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    data-testid="rally-ticket-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <ImageUploadField
                value={formData.image_url}
                mediaType={formData.image_media_type}
                onChange={(url, kind) => setFormData((prev) => ({ ...prev, image_url: url, image_media_type: kind }))}
                label="Banner Image or Video"
                testIdPrefix="rally-image"
                accept="both"
              />

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="rally-submit-btn"
                  className="btn-primary flex-1"
                >
                  {editingRally ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRallies;
